import { isNotNullish } from '@nx/stdlib';

import type {
  ConsumptionBreakdownItemResponse,
  ConsumptionDiscountResponse,
  ConsumptionResponse,
} from '../console.api.types';
import {
  CONSUMPTION_TYPE,
  CURRENCY,
  type Consumption,
  type ConsumptionBreakdownItem,
  type ConsumptionDiscount,
} from '../console.types';

const transformDiscountResponse = (response: ConsumptionDiscountResponse): ConsumptionDiscount => ({
  promotionName: response.promotion_name,
  voucherCode: response.voucher_code,
  dollarAmountOff: response.dollar_amount_off,
});

const transformBreakdownResponse = (response: ConsumptionBreakdownItemResponse): ConsumptionBreakdownItem => {
  if (response.consumption_type === 'database') {
    return {
      consumptionType: CONSUMPTION_TYPE.DATABASE,
      dbid: response.dbid,
      name: response.name,
      tier: response.tier,
      productName: response.product_name,
      pricingPlan: response.pricing_plan,
      usage: response.usage,
      cost: response.cost,
      currency: response.currency,
      unitCostInDollars: response.unit_cost_in_dollars,
      unitOfMeasure: response.unit_of_measure,
      billingStatus: response.billing_status,
    };
  }
  return {
    consumptionType: CONSUMPTION_TYPE.SESSION,
    sessionId: response.session_id,
    productName: response.product_name,
    usage: response.usage,
    cost: response.cost,
    currency: response.currency,
    unitCostInDollars: response.unit_cost_in_dollars,
    unitOfMeasure: response.unit_of_measure,
  };
};

const transformCurrency = (currency: string): CURRENCY => {
  if (currency === CURRENCY.DOLLARS.toString()) {
    return CURRENCY.DOLLARS;
  }

  if (currency === CURRENCY.CREDITS.toString()) {
    return CURRENCY.CREDITS;
  }

  throw new Error(`Invalid currency value in consumption reports: ${currency}`);
};

export const transformConsumptionResponse = (response: ConsumptionResponse): Consumption => ({
  totalCost: response.total_cost,
  currency: transformCurrency(response.currency),
  totalBreakdownCount: response.total_breakdown_count,
  breakdown: response.breakdown?.map((breakdown) => transformBreakdownResponse(breakdown)),
  usagePeriodEnd: response.usage_period_end,
  usagePeriodStart: response.usage_period_start,
  discount: isNotNullish(response.discount) ? transformDiscountResponse(response.discount) : undefined,
});
